import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { adminService } from '../../services/adminService'
import AdminLayout from '../../components/admin/AdminLayout'
import { devError } from '../../utils/devLog'
import Select from '../../components/ui/Select'
import Button from '../../components/ui/Button'

const PAYMENT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
]

export default function AdminOrders() {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const navigate = useNavigate()

  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchQuery.trim()
      setDebouncedSearch(trimmed)
      setPage((p) => (trimmed ? 1 : p))
    }, 500)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    fetchOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, debouncedSearch])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminService.getOrders({
        paymentStatus: statusFilter,
        page,
        limit: 20,
        search: debouncedSearch || undefined,
      })
      if (response.success) {
        setOrders(response.orders)
        setPagination(response.pagination)
      } else {
        setError(response.error || 'Failed to fetch orders')
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch orders. Please try again.'
      setError(errorMessage)
      devError('Error fetching orders', err)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      setError(null)
      await adminService.exportOrders({
        paymentStatus: statusFilter,
        search: debouncedSearch || undefined,
      })
      setSuccess('Orders exported successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      const errorMessage = err.message || 'Failed to export orders. Please try again.'
      setError(errorMessage)
      devError('Error exporting orders', err)
    } finally {
      setExporting(false)
    }
  }

  const handleOrderClick = (orderId) => {
    navigate(`/admin/orders/${orderId}`)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black mb-2">
                <span className="text-white">Orders</span>
              </h1>
              <p className="text-gray-400">Track payments and orders</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={PAYMENT_STATUS_OPTIONS}
                className="w-full md:w-48"
              />
              <Button
                onClick={handleExport}
                disabled={exporting || loading}
                loading={exporting}
                variant="outline"
                size="md"
                className="whitespace-nowrap"
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by customer name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setPage(1)
              }}
              className="w-full bg-gray-800 border border-gray-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-pitch-green placeholder-gray-500"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('')
                  setPage(1)
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg flex flex-wrap items-center justify-between gap-3">
              <span className="flex-1 min-w-0">{error}</span>
              <Button variant="outline" size="sm" onClick={fetchOrders}>
                Retry
              </Button>
            </div>
          )}
          {success && (
            <div className="bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No orders found. Orders will appear here once payment integration is added.
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <motion.div
                key={order._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => handleOrderClick(order._id)}
                className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6 hover:border-pitch-green/50 transition-all cursor-pointer"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Customer</p>
                    <p className="text-white font-semibold">
                      {order.submissionId?.name || 'N/A'}
                    </p>
                    <p className="text-gray-400 text-sm">{order.submissionId?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Plan</p>
                    <p className="text-white font-semibold">{order.planName}</p>
                    <p className="text-pitch-green text-lg font-bold">₹{order.amount}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Payment Status</p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                        order.paymentStatus === 'completed'
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : order.paymentStatus === 'failed'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}
                    >
                      {order.paymentStatus.toUpperCase()}
                    </span>
                    <p className="text-gray-400 text-xs mt-2">
                      {formatDate(order.paymentDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm mb-1">Order ID</p>
                    <p className="text-gray-400 text-xs font-mono break-all">{order._id}</p>
                    <p className="text-pitch-green text-xs mt-2 hover:underline">View Details →</p>
                  </div>
                </div>
              </motion.div>
            ))}
            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="outline"
                >
                  Previous
                </Button>
                <span className="text-gray-400">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  variant="outline"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </AdminLayout>
  )
}
